import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const TranscriptSegmentEditorDialog = ({
  open,
  onOpenChange,
  segment,
  segmentIndex,
  language = "es",
  loading = false,
  onSave,
}) => {
  const [start, setStart] = useState("0.0");
  const [end, setEnd] = useState("0.0");
  const [text, setText] = useState("");

  useEffect(() => {
    if (!open || !segment) return;
    setStart(String(Number(segment.start || 0).toFixed(2)));
    setEnd(String(Number(segment.end || 0).toFixed(2)));
    setText(segment.text || "");
  }, [open, segment]);

  const copy = useMemo(() => language === "es" ? {
    title: "Editar fragmento",
    description: "Corrige texto y marca el segundo exacto donde empieza y termina el audio.",
    start: "Inicio (seg)",
    end: "Fin (seg)",
    text: "Texto del fragmento",
    save: "Guardar cambios",
  } : {
    title: "Edit segment",
    description: "Fix the text and set the exact second where the audio starts and ends.",
    start: "Start (sec)",
    end: "End (sec)",
    text: "Segment text",
    save: "Save changes",
  }, [language]);

  const handleSave = () => {
    onSave?.({
      start: Number(start || 0),
      end: Number(end || 0),
      text,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="transcript-segment-editor-dialog">
        <DialogHeader>
          <DialogTitle data-testid="transcript-segment-editor-title">{copy.title}</DialogTitle>
          <DialogDescription data-testid="transcript-segment-editor-description">{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground" data-testid="transcript-segment-editor-meta">
            #{segmentIndex + 1}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="transcript-segment-start-input">{copy.start}</label>
              <Input
                id="transcript-segment-start-input"
                type="number"
                step="0.01"
                value={start}
                onChange={(event) => setStart(event.target.value)}
                data-testid="transcript-segment-start-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="transcript-segment-end-input">{copy.end}</label>
              <Input
                id="transcript-segment-end-input"
                type="number"
                step="0.01"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
                data-testid="transcript-segment-end-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="transcript-segment-textarea">{copy.text}</label>
            <Textarea
              id="transcript-segment-textarea"
              value={text}
              onChange={(event) => setText(event.target.value)}
              className="min-h-[220px]"
              data-testid="transcript-segment-textarea"
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading || !text.trim()}
              data-testid="transcript-segment-save-button"
            >
              {copy.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
